import { useState, useEffect, useRef } from "react";
import { FaTrash, FaEdit, FaCopy, FaGripVertical, FaClipboard, FaPaste } from "react-icons/fa";
import TextInput from "../../../components/TextInput";
import TextArea from "../../../components/TextArea";
import HologramModeSelect from "../../../components/HologramModeSelect";
import HologramBlinkFields from "../../../components/HologramBlinkFields";
import { getDefaultHologramState, normalizeHologramState, validateHologramState } from "../../../utils/hologramState";
import Sortable from "sortablejs";
import { convertToPng } from "../../../utils/imageUtils.js";

// ===== PRESSE-PAPIER DES QUESTIONS (copier / coller entre questionnaires) =====

const QUIZ_CLIPBOARD_KEY = "iximanager_quiz_clipboard";
// Champs fichiers d'une question, dupliqués sur le disque au collage (à la sauvegarde du questionnaire)
export const QUIZ_FILE_FIELDS = ["quizImg", "quizAudio", "quizHologram"];

/*** Lit la question copiée ({ folderName, quiz }) ou null */
export function readQuizClipboard() {
  try {
    const raw = localStorage.getItem(QUIZ_CLIPBOARD_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeQuizClipboard(clipboard) {
  try {
    localStorage.setItem(QUIZ_CLIPBOARD_KEY, JSON.stringify(clipboard));
    return true;
  } catch (e) {
    return false;
  }
}

/*** Fichiers déjà enregistrés d'une question, à dupliquer sous un nouveau nom */
function getQuizCopyFrom(quiz) {
  const copyFrom = { ...quiz._copyFrom };
  QUIZ_FILE_FIELDS.forEach((field) => {
    if (typeof quiz[field] === "string" && quiz[field]) {
      copyFrom[field] = copyFrom[field] || quiz[field];
    }
  });
  return copyFrom;
}

/*** Prochain quizID libre */
function getNextQuizID(quizzes) {
  return Math.max(0, ...(quizzes || []).map((q) => Number(q.quizID) || 0)) + 1;
}

// ===== UTILITAIRES DE BASE =====

export function getEmptyQuiz() {
  return {
    quizQuestion: "",
    quizNote: "",
    quizTextAudio: "",
    quizImg: "",
    quizHologram: "",
    quizHologramInteractable: false,
    quizHologramState: getDefaultHologramState(),
    quizAudio: "",
    quizChoices: [],
    quizIndicators: [],
    ordered: false,
  };
}

/**
 * Construit la question à enregistrer à partir du formulaire.
 * Les fichiers en attente (File) sont uploadés à la sauvegarde du questionnaire.
 */
export function buildQuizFromForm(currentQuiz, { pendingImg, pendingHologram, pendingAudio, quizID } = {}) {
  let finalOrdered = currentQuiz.ordered !== undefined ? currentQuiz.ordered : false;
  if (!finalOrdered && Array.isArray(currentQuiz.quizChoices)) {
    if (currentQuiz.quizChoices.some((c) => c.ordered === true)) finalOrdered = true;
  }

  const cleanedChoices = (currentQuiz.quizChoices || []).map(({ ordered, ...choiceRest }) => choiceRest);
  const quizHologram = pendingHologram || currentQuiz.quizHologram || "";

  return {
    quizQuestion: currentQuiz.quizQuestion,
    quizNote: currentQuiz.quizNote,
    quizTextAudio: currentQuiz.quizTextAudio || "",
    quizImg: pendingImg || currentQuiz.quizImg || "",
    quizHologram,
    // Sans hologramme, le mode n'a pas de sens : on le remet à "fixe"
    quizHologramInteractable: !!quizHologram && currentQuiz.quizHologramInteractable === true,
    // Sans hologramme, le clignotement repasse à l'état par défaut (Off, vitesse 1)
    quizHologramState: quizHologram ? normalizeHologramState(currentQuiz.quizHologramState) : getDefaultHologramState(),
    quizAudio: pendingAudio || currentQuiz.quizAudio || "",
    quizChoices: cleanedChoices,
    quizIndicators: (currentQuiz.quizIndicators || []).map(({ indicatorID, ...rest }) => rest),
    ordered: finalOrdered,
    quizID,
    // Question collée/dupliquée : conserver les fichiers à dupliquer à la sauvegarde
    ...(currentQuiz._copyFrom && { _copyFrom: currentQuiz._copyFrom }),
  };
}

export function getEmptyIndicator() {
  return {
    indicatorID: Date.now() + Math.random(),
    indicatorPrefab: [],
    indicatorPos: { x: 0, y: 0, z: 0 },
    indicatorRot: { x: 0, y: 0, z: 0 },
    indicatorPrefabScale: "1",
  };
}

// Une question doit avoir un quizID (les anciennes questions utilisaient "choiceID")
function needsQuizId(quiz) {
  return !quiz.quizID || quiz.choiceID !== undefined;
}

export function addTmpIdToQuiz(quiz) {
  if (!needsQuizId(quiz)) return quiz;
  // Migration : renomme l'ancien "choiceID" de la question en "quizID"
  const { choiceID, ...rest } = quiz;
  return { ...rest, quizID: rest.quizID || choiceID || Date.now() + Math.random() };
}

export function reindexQuizTmpIds(quizzes) {
  return quizzes.map((quiz, idx) => ({
    ...quiz,
    quizID: idx + 1,
  }));
}

// ===== GESTION DES CHOIX DE QUIZ =====

export function getInitialQuizChoice() {
  return {
    choiceID: Date.now() + Math.random(),
    choice: "",
    correct: false,
    answer: "",
    choiceFeedbacks: {
      feedbackPrefab: [],
      feedbackPos: { x: 0, y: 0, z: 0 },
      feedbackRot: { x: 0, y: 0, z: 0 },
      feedbackPrefabScale: "1",
    },
  };
}

let quizChoiceCounter = 1;
export function addTmpIdToQuizChoice(choice) {
  if (!choice.choiceID) {
    const numero = choice.numero || quizChoiceCounter++;
    return { ...choice, choiceID: Date.now() + Math.random(), numero };
  }
  if (!choice.numero) {
    return { ...choice, numero: quizChoiceCounter++ };
  }
  return choice;
}

export function handleDuplicateQuizChoice(
  choiceTmpId,
  currentQuiz,
  setCurrentQuiz,
  setMessage
) {
  if (!currentQuiz || !Array.isArray(currentQuiz.quizChoices)) return;
  const choiceToDuplicate = currentQuiz.quizChoices.find((choice) => choice.choiceID === choiceTmpId);
  if (!choiceToDuplicate) return;

  const duplicatedChoice = {
    ...choiceToDuplicate,
    choiceID: Date.now() + Math.random(),
    choice: `${choiceToDuplicate.choice}`,
  };

  setCurrentQuiz((prev) => ({
    ...prev,
    quizChoices: [...(prev.quizChoices || []), duplicatedChoice],
  }));

  setMessage({ type: "success", text: "Réponse dupliquée avec succès !" });
}

// ===== VALIDATION =====

export function validateQuiz(quiz) {
  const errors = [];
  if (!quiz.quizQuestion || quiz.quizQuestion.trim() === "") {
    errors.push("La question du quiz est obligatoire");
  }
  return { isValid: errors.length === 0, errors };
}

// ===== COMPOSANT QUIZ MANAGER =====

const QuizzesManage = ({
  Contents,
  setContents,
  editingTopicIndex,
  folderName,
  getNextTopicID,
  setMessage,
  onUpload,
  onDelete,
  detailsRef,
}) => {
  const [editingQuizIndex, setEditingQuizIndex] = useState(null);
  const [currentQuiz, setCurrentQuiz] = useState(getEmptyQuiz());
  const [currentQuizChoice, setCurrentQuizChoice] = useState(getInitialQuizChoice());
  const [editingQuizChoiceIndex, setEditingQuizChoiceIndex] = useState(null);
  const [isSorted, setIsSorted] = useState(false);

  // États dédiés pour les indicateurs
  const [currentIndicator, setCurrentIndicator] = useState(getEmptyIndicator());
  const [editingIndicatorIndex, setEditingIndicatorIndex] = useState(null);

  const quizzesListRef = useRef(null);
  const sortableRef = useRef(null);
  const answerRefs = useRef([]);
  const [showTransformMenu, setShowTransformMenu] = useState(false);
  const [showIndicatorTransformMenu, setShowIndicatorTransformMenu] = useState(false);

  // État pour les fichiers en attente (uploadés à la sauvegarde du questionnaire)
  const [pendingImg, setPendingImg] = useState(null);
  const [pendingAudio, setPendingAudio] = useState(null);
  const [pendingHologram, setPendingHologram] = useState(null);
  const [previewImgUrl, setPreviewImgUrl] = useState(null);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
  const [isImgModalOpen, setIsImgModalOpen] = useState(false);
  const [imgModalSrc, setImgModalSrc] = useState(null);

  const openImgModal = (src) => {
    setImgModalSrc(src);
    setIsImgModalOpen(true);
  };

  const closeImgModal = () => {
    setIsImgModalOpen(false);
    setImgModalSrc(null);
  };

  const resetPendingFiles = () => {
    setPendingImg(null);
    setPendingAudio(null);
    setPendingHologram(null);
  };

  useEffect(() => {
    if (!pendingImg) {
      setPreviewImgUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingImg);
    setPreviewImgUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingImg]);

  useEffect(() => {
    if (!pendingAudio) {
      setPreviewAudioUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingAudio);
    setPreviewAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingAudio]);

  // Fichier choisi mais pas encore uploadé (sauvegarde du questionnaire en attente)
  const isPendingFile = (value) => value instanceof File || value instanceof Blob;

  // Question copiée (partagée entre les questionnaires et les onglets via localStorage)
  const [quizClipboard, setQuizClipboard] = useState(readQuizClipboard);
  const canPasteQuiz = !!quizClipboard?.quiz && quizClipboard.folderName === folderName;

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === QUIZ_CLIPBOARD_KEY) setQuizClipboard(readQuizClipboard());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Raccourcis lecture prefab
  const prefab = currentQuizChoice.choiceFeedbacks?.feedbackPrefab || [];
  const indicatorPrefabArr = Array.isArray(currentIndicator.indicatorPrefab)
    ? currentIndicator.indicatorPrefab
    : currentIndicator.indicatorPrefab
      ? [currentIndicator.indicatorPrefab]
      : [];

  // ===== HANDLER indicatorPrefab =====
  const handleIndicatorChange = (type, value) => {
    setCurrentIndicator((prev) => {
      const currentPrefab = Array.isArray(prev.indicatorPrefab)
        ? prev.indicatorPrefab
        : prev.indicatorPrefab ? [prev.indicatorPrefab] : [];

      if (type === "main") {
        return { ...prev, indicatorPrefab: [value] };
      } else if (type === "direction") {
        const newPrefab = [currentPrefab[0], value, currentPrefab[2], ...currentPrefab.slice(3)].filter(Boolean);
        return { ...prev, indicatorPrefab: newPrefab };
      } else if (type === "orientation") {
        const arr = [...currentPrefab];
        arr[2] = value;
        return { ...prev, indicatorPrefab: arr.slice(0, 3).filter(Boolean) };
      } else if (type === "distance") {
        const base = currentPrefab.filter((v) => v !== "distance");
        if (value) base.push("distance");
        return { ...prev, indicatorPrefab: base };
      } else if (["x", "y", "z"].includes(type)) {
        return { ...prev, indicatorPos: { ...prev.indicatorPos, [type]: value } };
      } else if (["rx", "ry", "rz"].includes(type)) {
        return { ...prev, indicatorRot: { ...prev.indicatorRot, [type.replace("r", "")]: value } };
      } else {
        return { ...prev, [type]: value };
      }
    });
  };

  // ===== HANDLER feedbackPrefab =====
  const handleChoiceFeedbackPrefabChange = (type, value) => {
    setCurrentQuizChoice((prev) => {
      const fb = prev.choiceFeedbacks.feedbackPrefab || [];
      let newPrefab;

      if (type === "main") {
        newPrefab = [value];
      } else if (type === "direction") {
        newPrefab = [fb[0], value, fb[2], ...fb.slice(3)].filter(Boolean);
      } else if (type === "orientation") {
        const arr = [...fb];
        arr[2] = value;
        newPrefab = arr.slice(0, 3).filter(Boolean);
      } else if (type === "distance") {
        const base = fb.filter((v) => v !== "distance");
        if (value) base.push("distance");
        newPrefab = base;
      } else {
        return prev;
      }

      return {
        ...prev,
        choiceFeedbacks: { ...prev.choiceFeedbacks, feedbackPrefab: newPrefab },
      };
    });
  };

  // ===== SORTABLE + IDs =====
  useEffect(() => {
    if (!Contents?.quizzes || !currentQuiz?.quizChoices) return;

    let quizzesUpdated = false;
    let quizChoicesUpdated = false;

    const updatedQuizzes = Contents.quizzes.map((quiz) => {
      if (needsQuizId(quiz)) {
        quizzesUpdated = true;
        return addTmpIdToQuiz(quiz);
      }
      return quiz;
    });

    if (quizzesUpdated) {
      setContents((prev) => ({ ...prev, quizzes: updatedQuizzes }));
    }

    const updatedChoices = currentQuiz.quizChoices.map((choice, index) => {
      if (!choice.choiceID) {
        quizChoicesUpdated = true;
        return {
          ...choice,
          choiceID: `choice-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        };
      }
      return choice;
    });

    if (quizChoicesUpdated) {
      setCurrentQuiz((prev) => ({ ...prev, quizChoices: updatedChoices }));
    }

    if (quizzesListRef.current && Contents.quizzes.length > 1) {
      if (quizzesListRef.current._sortable) {
        quizzesListRef.current._sortable.destroy();
        quizzesListRef.current._sortable = null;
      }
      quizzesListRef.current._sortable = Sortable.create(quizzesListRef.current, {
        animation: 150,
        handle: ".drag-handle",
        onEnd: (evt) => {
          if (evt.oldIndex === evt.newIndex) return;
          const nodes = Array.from(quizzesListRef.current.children);
          const newOrder = nodes.map((node) => node.getAttribute("data-id"));
          const newQuizzes = newOrder
            .map((id) => Contents.quizzes.find((q) => String(q.quizID) === String(id)))
            .filter(Boolean);
          if (newQuizzes.length === Contents.quizzes.length) {
            setContents((prev) => ({ ...prev, quizzes: newQuizzes }));
          }
        },
      });
    }

    if (sortableRef.current && currentQuiz.quizChoices.length > 1) {
      if (sortableRef.current._sortable) {
        sortableRef.current._sortable.destroy();
        sortableRef.current._sortable = null;
      }
      sortableRef.current._sortable = Sortable.create(sortableRef.current, {
        animation: 150,
        handle: ".drag-handle",
        onEnd: (evt) => {
          if (evt.oldIndex === evt.newIndex) return;
          const nodes = Array.from(sortableRef.current.children);
          const newOrder = nodes.map((node) => node.getAttribute("data-id"));
          const reorderedChoices = newOrder
            .map((id) => currentQuiz.quizChoices.find((c) => String(c.choiceID) === String(id)))
            .filter(Boolean);
          if (reorderedChoices.length === currentQuiz.quizChoices.length) {
            setCurrentQuiz((prev) => ({ ...prev, quizChoices: reorderedChoices }));
          }
        },
      });
    }

    return () => {
      if (quizzesListRef.current?._sortable) {
        quizzesListRef.current._sortable.destroy();
        quizzesListRef.current._sortable = null;
      }
      if (sortableRef.current?._sortable) {
        sortableRef.current._sortable.destroy();
        sortableRef.current._sortable = null;
      }
    };
  }, [Contents?.quizzes, currentQuiz?.quizChoices, setContents, setCurrentQuiz]);

  // ===== SYNC quiz à éditer =====
  useEffect(() => {
    if (editingQuizIndex !== null && Array.isArray(Contents.quizzes)) {
      const quizToEdit = Contents.quizzes.find((q) => q.quizID === editingQuizIndex);
      if (quizToEdit) setCurrentQuiz({ ...quizToEdit });
    }
    if (editingQuizIndex === null) setCurrentQuiz(getEmptyQuiz());
    setCurrentIndicator(getEmptyIndicator());
    setEditingIndicatorIndex(null);
  }, [editingQuizIndex, Contents.quizzes]);

  // ===== IDs manquants =====
  useEffect(() => {
    if (Contents?.quizzes && Array.isArray(Contents.quizzes)) {
      if (Contents.quizzes.some(needsQuizId)) {
        setContents((prev) => ({ ...prev, quizzes: prev.quizzes.map(addTmpIdToQuiz) }));
      }
    }
  }, [Contents?.quizzes]);

  // ===== HANDLERS QUIZ =====
  const handleCancelQuiz = () => {
    setCurrentQuiz(getEmptyQuiz());
    setEditingQuizIndex(null);
    setCurrentIndicator(getEmptyIndicator());
    setEditingIndicatorIndex(null);
    resetPendingFiles();
    detailsRef.current?.removeAttribute("open");
  };

  const handleAddQuiz = () => {
    // Vitesse de clignotement de l'hologramme (uniquement si un hologramme est présent)
    if (pendingHologram || currentQuiz.quizHologram) {
      const holoCheck = validateHologramState(currentQuiz.quizHologramState);
      if (!holoCheck.isValid) {
        setMessage({ type: "error", text: holoCheck.error });
        return;
      }
    }
    const nextId = (Contents?.quizzes?.length || 0) + 1;

    const newQuiz = buildQuizFromForm(currentQuiz, {
      pendingImg,
      pendingHologram,
      pendingAudio,
      quizID: editingQuizIndex !== null ? editingQuizIndex : nextId,
    });
    setContents((prev) => {
      const updatedContents = {
        ...prev,
        quizzes:
          editingQuizIndex !== null
            ? prev.quizzes.map((quiz) => (quiz.quizID === editingQuizIndex ? newQuiz : quiz))
            : [...(prev.quizzes || []), newQuiz],
      };
      detailsRef.current?.removeAttribute("open");
      return updatedContents;
    });
    setCurrentQuiz(getEmptyQuiz());
    setEditingQuizIndex(null);
    setCurrentIndicator(getEmptyIndicator());
    setEditingIndicatorIndex(null);
    resetPendingFiles();
    setMessage({ type: "success", text: "Quiz enregistré avec succès !" });
  };

  const handleDuplicateQuiz = (quizTmpId) => {
    const quizToDuplicate = Contents.quizzes.find((quiz) => quiz.quizID === quizTmpId);
    if (!quizToDuplicate) return;
    const nextId = (Contents?.quizzes?.length || 0) + 1;
    // Fichiers déjà enregistrés : dupliqués sous un nouveau nom à la sauvegarde du questionnaire
    const copyFrom = getQuizCopyFrom(quizToDuplicate);
    const duplicatedQuiz = {
      ...quizToDuplicate,
      quizID: nextId,
      quizQuestion: `${quizToDuplicate.quizQuestion} (copie)`,
      ...(Object.keys(copyFrom).length > 0 && { _copyFrom: copyFrom }),
    };
    setContents((prev) => ({
      ...prev,
      quizzes: [...(prev.quizzes || []), duplicatedQuiz],
    }));
    setMessage({ type: "success", text: "Quiz dupliqué avec succès !" });
  };

  const handleCopyQuiz = (quizTmpId) => {
    const quizToCopy = Contents.quizzes.find((quiz) => quiz.quizID === quizTmpId);
    if (!quizToCopy) return;

    // Un fichier choisi mais pas encore uploadé ne peut pas être mis en mémoire
    if (QUIZ_FILE_FIELDS.some((field) => quizToCopy[field] && typeof quizToCopy[field] !== "string")) {
      setMessage({ type: "error", text: "Sauvegardez le questionnaire avant de copier cette question (fichier en attente d'envoi)." });
      return;
    }

    const { _copyFrom, ...quiz } = quizToCopy;
    const clipboard = { folderName, quiz };
    if (!writeQuizClipboard(clipboard)) {
      setMessage({ type: "error", text: "Impossible de copier la question (stockage du navigateur indisponible)." });
      return;
    }
    setQuizClipboard(clipboard);
    setMessage({ type: "success", text: `Question "${quiz.quizQuestion}" copiée ! Ouvrez un autre questionnaire pour la coller.` });
  };

  const handlePasteQuiz = () => {
    if (!canPasteQuiz) return;
    const { quiz } = quizClipboard;

    // Les fichiers pointent encore vers ceux de la question d'origine :
    // ils seront dupliqués sous un nouveau nom à la sauvegarde du questionnaire
    const copyFrom = getQuizCopyFrom(quiz);
    const pastedQuiz = {
      ...quiz,
      quizID: getNextQuizID(Contents.quizzes),
      ...(Object.keys(copyFrom).length > 0 && { _copyFrom: copyFrom }),
    };

    setContents((prev) => ({
      ...prev,
      quizzes: [...(prev.quizzes || []), pastedQuiz],
    }));
    setMessage({ type: "success", text: `Question "${quiz.quizQuestion}" collée ! Sauvegardez le questionnaire pour l'enregistrer.` });
  };

  const handleDeleteQuiz = (quizTmpId) => {
    if (!window.confirm("⚠️ Êtes-vous sûr de vouloir supprimer ce quiz ?")) return;
    setContents((prev) => ({
      ...prev,
      quizzes: prev.quizzes.filter((quiz) => quiz.quizID !== quizTmpId),
    }));
    if (editingQuizIndex === quizTmpId) {
      setCurrentQuiz(getEmptyQuiz());
      setEditingQuizIndex(null);
      resetPendingFiles();
    }
    setMessage({ type: "success", text: "Question supprimée avec succès !" });
  };

  // Retire le fichier de la question (il sera écrasé au prochain upload)
  const deleteQuizFile = (fieldName) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce fichier ?\n\n" + "👉 " + fieldName)) return;
    // Sans hologramme, le mode fixe/manipulable est remis à "fixe" et le clignotement à Off
    const cleared = fieldName === "quizHologram"
      ? { [fieldName]: "", quizHologramInteractable: false, quizHologramState: getDefaultHologramState() }
      : { [fieldName]: "" };
    setCurrentQuiz((prev) => ({ ...prev, ...cleared }));
    if (editingQuizIndex !== null) {
      setContents((prev) => ({
        ...prev,
        quizzes: prev.quizzes.map((quiz) =>
          quiz.quizID === editingQuizIndex ? { ...quiz, ...cleared } : quiz
        ),
      }));
    }
  };

  // ===== HANDLERS INDICATEURS =====
  const handleAddIndicator = () => {
    const indicatorWithId = {
      ...currentIndicator,
      indicatorID: currentIndicator.indicatorID || Date.now() + Math.random(),
    };

    setCurrentQuiz((prev) => {
      const newIndicators =
        editingIndicatorIndex !== null
          ? (prev.quizIndicators || []).map((ind, idx) =>
            idx === editingIndicatorIndex ? indicatorWithId : ind
          )
          : [...(prev.quizIndicators || []), indicatorWithId];
      return { ...prev, quizIndicators: newIndicators };
    });

    setCurrentIndicator(getEmptyIndicator());
    setEditingIndicatorIndex(null);
    setMessage({ type: "success", text: "Indicateur ajouté !" });
  };

  const handleEditIndicator = (idx) => {
    const ind = currentQuiz.quizIndicators[idx];
    setCurrentIndicator({
      ...ind,
      indicatorPrefab: Array.isArray(ind.indicatorPrefab)
        ? ind.indicatorPrefab
        : ind.indicatorPrefab
          ? [ind.indicatorPrefab]
          : [],
    });
    setEditingIndicatorIndex(idx);
  };

  const handleRemoveIndicator = (idx) => {
    if (!window.confirm("⚠️ Êtes-vous sûr de vouloir supprimer cet indicateur ?")) return;
    setCurrentQuiz((prev) => ({
      ...prev,
      quizIndicators: prev.quizIndicators.filter((_, i) => i !== idx),
    }));
  };

  const handleCancelIndicatorEdit = () => {
    setCurrentIndicator(getEmptyIndicator());
    setEditingIndicatorIndex(null);
  };

  // ===== HANDLERS CHOIX =====
  const handleAddQuizChoice = () => {
    if (!currentQuizChoice.choice) {
      setMessage({ type: "error", text: "Le choix est obligatoire." });
      return;
    }
    setCurrentQuiz((prev) => {
      let newChoices =
        editingQuizChoiceIndex !== null
          ? prev.quizChoices.map((choice, idx) =>
            idx === editingQuizChoiceIndex ? addTmpIdToQuizChoice(currentQuizChoice) : choice
          )
          : prev.quizChoices
            ? [...prev.quizChoices, addTmpIdToQuizChoice(currentQuizChoice)]
            : [addTmpIdToQuizChoice(currentQuizChoice)];
      if (isSorted) {
        newChoices = [...newChoices].sort((a, b) =>
          a.choice.localeCompare(b.choice, "fr", { sensitivity: "base" })
        );
        setIsSorted(false);
      }
      return { ...prev, quizChoices: newChoices };
    });
    setCurrentQuizChoice(getInitialQuizChoice());
    setEditingQuizChoiceIndex(null);
  };

  const handleRemoveQuizChoice = (idx) => {
    if (!window.confirm("⚠️ Êtes-vous sûr de vouloir supprimer cette réponse ?")) return;
    setCurrentQuiz((prev) => ({
      ...prev,
      quizChoices: prev.quizChoices.filter((_, i) => i !== idx),
    }));
  };

  const handleEditQuizChoice = (idx) => {
    const choiceToEdit = currentQuiz.quizChoices[idx];
    const migratedChoice = {
      ...choiceToEdit,
      choiceFeedbacks: {
        ...choiceToEdit.choiceFeedbacks,
        feedbackPrefab: Array.isArray(choiceToEdit.choiceFeedbacks?.feedbackPrefab)
          ? choiceToEdit.choiceFeedbacks.feedbackPrefab
          : choiceToEdit.choiceFeedbacks?.feedbackPrefab
            ? [choiceToEdit.choiceFeedbacks.feedbackPrefab]
            : [],
      },
    };
    setCurrentQuizChoice({ ...migratedChoice });
    setEditingQuizChoiceIndex(idx);
  };

  const handleCancelQuizChoiceEdit = () => {
    setCurrentQuizChoice(getInitialQuizChoice());
    setEditingQuizChoiceIndex(null);
  };

  const handleQuizChoiceChange = (field, value) => {
    if (field === "choice" || field === "correct" || field === "answer") {
      setCurrentQuizChoice({
        ...currentQuizChoice,
        [field]: field === "correct" ? value === "true" : value,
      });
    } else if (
      ["x", "y", "z"].includes(field) &&
      currentQuizChoice.choiceFeedbacks.feedbackPos !== undefined
    ) {
      setCurrentQuizChoice({
        ...currentQuizChoice,
        choiceFeedbacks: {
          ...currentQuizChoice.choiceFeedbacks,
          feedbackPos: { ...currentQuizChoice.choiceFeedbacks.feedbackPos, [field]: value },
        },
      });
    } else if (["rx", "ry", "rz"].includes(field)) {
      setCurrentQuizChoice({
        ...currentQuizChoice,
        choiceFeedbacks: {
          ...currentQuizChoice.choiceFeedbacks,
          feedbackRot: {
            ...currentQuizChoice.choiceFeedbacks.feedbackRot,
            [field.replace("r", "")]: value,
          },
        },
      });
    } else {
      setCurrentQuizChoice({
        ...currentQuizChoice,
        choiceFeedbacks: { ...currentQuizChoice.choiceFeedbacks, [field]: value },
      });
    }
  };

  // ===== RENDER =====
  return (
    <div className="mt-4 p-4 border-primary rounded" style={{ borderStyle: "dashed", borderWidth: "1px" }}>

      {/* LISTE DES QUIZZES */}
      {(Contents?.quizzes?.length || 0) > 0 ? (
        <div>
          <h3 className="text-primary text-center">Liste des questions</h3>
          <p className="small text-center">Questions à répondre lors de cette étape.</p>
          <div ref={quizzesListRef}>
            {(Contents?.quizzes || []).map((quiz, index) => (
              <div
                className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
                key={quiz.quizID || `quiz-fallback-${index}`}
                data-id={quiz.quizID || `quiz-fallback-${index}`}
              >
                <span className="drag-handle me-2" style={{ cursor: "grab" }} title="Déplacer">
                  <FaGripVertical />
                </span>
                <div className="flex-grow-1">
                  <strong>{quiz.quizQuestion || "⚠️ Sans question"}</strong>
                  <div className="text-muted small">{quiz.quizNote}</div>
                </div>
                <div>
                  <button type="button" className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => {
                      setEditingQuizIndex(quiz.quizID);
                      resetPendingFiles();
                      if (detailsRef.current) {
                        detailsRef.current.setAttribute("open", true);
                        detailsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                      }
                    }} title="Modifier la question">
                    <FaEdit />
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary me-2"
                    onClick={() => handleDuplicateQuiz(quiz.quizID)} title="Dupliquer la question">
                    <FaCopy />
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-info me-2"
                    onClick={() => handleCopyQuiz(quiz.quizID)} title="Copier la question pour la coller dans un autre questionnaire">
                    <FaClipboard />
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDeleteQuiz(quiz.quizID)} title="Supprimer la question">
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <h3 className="text-primary text-center">Pas de Question</h3>
          <p className="small text-center"><b>Aucune question pour cette étape</b></p>
        </div>
      )}

      {canPasteQuiz && (
        <div className="text-center mb-3">
          <button type="button" className="btn btn-sm btn-outline-info"
            onClick={handlePasteQuiz}
            title="Ajoute une copie de la question en mémoire à la fin de ce questionnaire">
            <FaPaste className="me-1" /> Coller la question "{quizClipboard.quiz.quizQuestion || "Sans question"}"
          </button>
        </div>
      )}

      {/* FORMULAIRE D'ÉDITION */}
      <details ref={detailsRef} id="quizEditForm">
        <summary className="fw-bold fs-5 text-primary text-center mb-4">
          {editingQuizIndex !== null ? (
            <>Modifier la question{" "}
              {Contents.quizzes.find((q) => q.quizID === editingQuizIndex)?.quizQuestion && (
                <span>"{Contents.quizzes.find((q) => q.quizID === editingQuizIndex)?.quizQuestion}"</span>
              )}
            </>
          ) : (Contents?.quizzes?.length || 0) > 0 ? <>Ajouter une question</> : <>Créer une question</>}
        </summary>

        <div className="border rounded p-4">

          {/* Question / Note / Texte audio */}
          <div className="row">
            <div className="col-md-4">
              <TextArea
                label="Question (obligatoire)"
                value={currentQuiz.quizQuestion || ""}
                onChange={(e) => setCurrentQuiz({ ...currentQuiz, quizQuestion: e.target.value })}
                className="mb-2"
              />
            </div>
            <div className="col-md-4">
              <TextArea
                label="Note ℹ️"
                value={currentQuiz.quizNote || ""}
                onChange={(e) => setCurrentQuiz({ ...currentQuiz, quizNote: e.target.value })}
                className="mb-2"
              />
            </div>
            <div className="col-md-4" title="Texte à convertir en commentaire audio pour cette question.">
              <TextArea
                label="Texte Audio ℹ️"
                value={currentQuiz.quizTextAudio || ""}
                onChange={(e) => setCurrentQuiz({ ...currentQuiz, quizTextAudio: e.target.value })}
                className="mb-2"
              />
            </div>
          </div>

          {/* Image / Hologramme / Audio */}
          <div className="row mb-4">
            <div className="col-md-4" title="Image de la question (optionnelle).">
              <label className="form-label">
                <i className="mdi mdi-image-frame" /> Image de la question (png/jpg) ℹ️
              </label>
              <div className="d-flex align-items-center">
                <input
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  className="form-control"
                  style={currentQuiz.quizImg || previewImgUrl ? { width: "140px" } : undefined}
                  title={currentQuiz.quizImg ? "♻️ Remplacer le fichier existant" : "📥 Ajouter un fichier"}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const pngFile = await convertToPng(file);
                      setPendingImg(pngFile);
                    }
                  }}
                />
                {previewImgUrl ? (
                  <img
                    src={previewImgUrl}
                    alt="Vignette"
                    style={{ maxWidth: "60px", maxHeight: "60px", marginLeft: "10px", borderRadius: "4px", border: "1px solid #ccc", cursor: "zoom-in" }}
                    onClick={() => openImgModal(previewImgUrl)}
                  />
                ) : currentQuiz.quizImg ? (
                  <div className="d-flex align-items-center">
                    {isPendingFile(currentQuiz.quizImg) ? (
                      <div className="text-danger small fw-bold ms-2">
                        [Fichier présent]<br />en attente de sauvegarde...
                      </div>
                    ) : (
                      <img
                        src={`/api/data/${folderName}/${currentQuiz.quizImg}`}
                        alt="Vignette"
                        style={{ maxWidth: "60px", maxHeight: "60px", marginLeft: "10px", borderRadius: "4px", border: "1px solid #ccc", cursor: "zoom-in" }}
                        onClick={() => openImgModal(`/api/data/${folderName}/${currentQuiz.quizImg}`)}
                      />
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={() => deleteQuizFile("quizImg")}
                      title={`❌ Supprimer le fichier existant\n(${isPendingFile(currentQuiz.quizImg) ? "en attente" : currentQuiz.quizImg})`}
                    >
                      <FaTrash />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="col-md-4" title="[OPTION] Hologramme de la question (glb).
              👉 Le pivot de l'objet 3D est le même que celui du modèle principal
              afin de garantir un placement correct.">
              <label className="form-label">
                <i className="mdi mdi-cube-outline" /> Hologramme de la question (glb) ℹ️
              </label>
              <div className="d-flex align-items-center">
                <input
                  type="file"
                  accept=".glb"
                  className="form-control"
                  style={currentQuiz.quizHologram || pendingHologram ? { width: "140px" } : undefined}
                  title={currentQuiz.quizHologram ? "♻️ Remplacer le fichier existant" : "📥 Ajouter un fichier"}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setPendingHologram(file);
                  }}
                />
                {pendingHologram ? (
                  <span className="text-danger small fw-bold ms-2">[Nouveau fichier]<br />en attente de sauvegarde...</span>
                ) : currentQuiz.quizHologram ? (
                  <div className="d-flex align-items-center">
                    {isPendingFile(currentQuiz.quizHologram) ? (
                      <span className="text-danger small fw-bold ms-2">[Fichier présent]<br />en attente de sauvegarde...</span>
                    ) : (
                      <span className="text-primary small fw-bold ms-2">[Fichier existant]</span>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={() => deleteQuizFile("quizHologram")}
                      title={`❌ Supprimer le fichier existant\n(${isPendingFile(currentQuiz.quizHologram) ? "en attente" : currentQuiz.quizHologram})`}
                    >
                      <FaTrash />
                    </button>
                  </div>
                ) : null}
              </div>
              {(!!pendingHologram || !!currentQuiz.quizHologram) && (
                <HologramModeSelect
                  value={currentQuiz.quizHologramInteractable === true}
                  onChange={(val) => setCurrentQuiz((prev) => ({ ...prev, quizHologramInteractable: val }))}
                  fixedTitle={"L'hologramme de la question remplace temporairement l'hologramme principal\n(ex : un ordinateur sans sa plaque de fond.)"}
                  interactableTitle={"L'hologramme de la question est manipulable par l'utilisateur.\nDédié aux petits objets sur lesquels portent les actions\n(ex : un disque sur lequel on doit fixer un chassis)"}
                />
              )}
              {(!!pendingHologram || !!currentQuiz.quizHologram) && (
                <HologramBlinkFields
                  value={currentQuiz.quizHologramState}
                  onChange={(state) => setCurrentQuiz((prev) => ({ ...prev, quizHologramState: state }))}
                />
              )}
            </div>

            <div className="col-md-4" title="Fichier audio du commentaire de la question.">
              <label className="form-label">
                <i className="mdi mdi-volume-high" /> Audio (ogg) ℹ️
              </label>
              <input
                type="file"
                accept=".ogg"
                className="form-control"
                title={currentQuiz.quizAudio ? "♻️ Remplacer le fichier existant" : "📥 Ajouter un fichier audio"}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) setPendingAudio(file);
                }}
              />
              {previewAudioUrl ? (
                <audio controls src={previewAudioUrl} style={{ maxWidth: "100%", marginTop: "6px" }}>
                  Votre navigateur ne supporte pas l'audio.
                </audio>
              ) : currentQuiz.quizAudio ? (
                <div className="d-flex align-items-center mt-1">
                  {isPendingFile(currentQuiz.quizAudio) ? (
                    <div className="text-danger small fw-bold">
                      [Fichier présent] en attente de sauvegarde...
                    </div>
                  ) : (
                    <audio controls src={`/api/data/${folderName}/${currentQuiz.quizAudio}`} style={{ maxWidth: "calc(100% - 40px)" }}>
                      Votre navigateur ne supporte pas l'audio.
                    </audio>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger ms-2"
                    onClick={() => deleteQuizFile("quizAudio")}
                    title={`❌ Supprimer le fichier existant\n(${isPendingFile(currentQuiz.quizAudio) ? "en attente" : currentQuiz.quizAudio})`}
                  >
                    <FaTrash />
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {/* ===== SECTION INDICATEURS ===== */}
          <div className="border border-secondary rounded p-3 mb-4">
            <h4 className="text-secondary text-center">Indicateurs</h4>
            <p className="small text-secondary text-center">
              Éléments visuels <b>optionnels</b> positionnés dans l'espace 3D.
            </p>

            <div className="row">
              {/* GAUCHE — formulaire indicateur */}
              <div className="col">
                <div className="row">

                  {/* Type principal */}
                  <div className="col-md-8" title="Ajoutez des indicateurs positionnés sur le modèle 3D">
                    <label className="form-label">
                      <i className="mdi mdi-map-marker-outline" /> Type d'indicateur ℹ️
                    </label>
                    <div className="d-flex gap-2 mb-2 flex-wrap">
                      {["zone", "niveau", "aplomb", "point"].map((opt) => (
                        <button key={opt} type="button"
                          className={`btn btn-sm ${indicatorPrefabArr[0] === opt ? "btn-secondary" : "btn-outline-secondary"}`}
                          onClick={() => handleIndicatorChange("main", opt)}>
                          {opt}
                        </button>
                      ))}
                      {indicatorPrefabArr.length > 0 && (
                        <button type="button" className="btn btn-sm btn-outline-danger"
                          onClick={() => setCurrentIndicator((prev) => ({ ...prev, indicatorPrefab: [] }))}
                          title="Retirer l'indicateur">
                          ✕ Aucun
                        </button>
                      )}
                    </div>

                    {/* Sous-options "point" */}
                    {indicatorPrefabArr[0] === "point" && (
                      <div className="ps-2 border-start border-secondary mt-2">
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <small className="text-secondary">Direction :</small>
                          {["in", "out"].map((opt) => (
                            <button key={opt} type="button"
                              className={`btn btn-sm ${indicatorPrefabArr[1] === opt ? "btn-secondary" : "btn-outline-secondary"}`}
                              onClick={() => handleIndicatorChange("direction", opt)}>
                              {opt}
                            </button>
                          ))}
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <small className="text-secondary">Orientation :</small>
                          {[["vertical", "Vertical"], ["horizontal", "Horizontal"]].map(([val, label]) => (
                            <button key={val} type="button"
                              className={`btn btn-sm ${indicatorPrefabArr[2] === val ? "btn-secondary" : "btn-outline-secondary"}`}
                              onClick={() => handleIndicatorChange("orientation", val)}>
                              {label}
                            </button>
                          ))}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <small className="text-secondary">Distance :</small>
                          <button type="button"
                            className={`btn btn-sm ${indicatorPrefabArr.includes("distance") ? "btn-secondary" : "btn-outline-secondary"}`}
                            onClick={() => handleIndicatorChange("distance", !indicatorPrefabArr.includes("distance"))}>
                            {indicatorPrefabArr.includes("distance") ? "✔ Oui" : "Non"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Échelle */}
                  <div className="col-md-2" title="Échelle de l'indicateur (base : 1cm)">
                    <TextInput
                      label="Échelle ℹ️"
                      type="text"
                      value={String(currentIndicator.indicatorPrefabScale ?? "1")}
                      onChange={(e) => handleIndicatorChange("indicatorPrefabScale", e.target.value)}
                    />
                  </div>

                  {/* Position / Rotation */}
                  {indicatorPrefabArr.length > 0 && (
                    <div className="col-md-12 mt-3">
                      <button type="button"
                        className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                        onClick={() => setShowIndicatorTransformMenu(!showIndicatorTransformMenu)}>
                        <span><i className="mdi mdi-cog-outline me-2" />Position & Rotation</span>
                        <i className={`mdi ${showIndicatorTransformMenu ? "mdi-chevron-up" : "mdi-chevron-down"}`} />
                      </button>
                      {showIndicatorTransformMenu && (
                        <div className="mt-3 p-3 border rounded bg-light">
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label small fw-bold">
                                <i className="mdi mdi-axis-arrow" /> Position (x, y, z)
                              </label>
                              <div className="d-flex gap-2">
                                {["x", "y", "z"].map((axis) => (
                                  <div key={axis} className="flex-fill">
                                    <TextInput label={axis.toUpperCase()} type="text"
                                      value={String(currentIndicator.indicatorPos?.[axis] ?? 0)}
                                      onChange={(e) => handleIndicatorChange(axis, e.target.value)} />
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-bold">
                                <i className="mdi mdi-rotate-3d" /> Rotation (x, y, z)
                              </label>
                              <div className="d-flex gap-2">
                                {[["rx", "X"], ["ry", "Y"], ["rz", "Z"]].map(([key, label]) => (
                                  <div key={key} className="flex-fill">
                                    <TextInput label={label} type="text"
                                      value={String(currentIndicator.indicatorRot?.[key.replace("r", "")] ?? 0)}
                                      onChange={(e) => handleIndicatorChange(key, e.target.value)} />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Boutons Ajouter / Annuler */}
                  <div className="col d-flex justify-content-end my-3">
                    {editingIndicatorIndex !== null && (
                      <button type="button" className="btn btn-sm btn-outline-secondary mt-3 me-2"
                        onClick={handleCancelIndicatorEdit}>
                        Annuler
                      </button>
                    )}
                    <button type="button" className="btn btn-sm btn-outline-secondary mt-3"
                      onClick={handleAddIndicator}>
                      {editingIndicatorIndex !== null ? "Modifier l'indicateur ▶" : "+ Ajouter l'indicateur ▶"}
                    </button>
                  </div>
                </div>
              </div>

              {/* DROITE — liste des indicateurs */}
              <div className="col">
                {Array.isArray(currentQuiz.quizIndicators) && currentQuiz.quizIndicators.length > 0 ? (
                  <div>
                    <label>Liste des indicateurs</label>
                    <div>
                      {currentQuiz.quizIndicators.map((ind, idx) => {
                        const indPrefab = Array.isArray(ind.indicatorPrefab)
                          ? ind.indicatorPrefab
                          : ind.indicatorPrefab ? [ind.indicatorPrefab] : [];
                        return (
                          <div key={ind.indicatorID || idx}
                            className="border rounded p-2 mb-1 d-flex align-items-center">
                            <button type="button" className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEditIndicator(idx)} title="Modifier">
                              <FaEdit />
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger me-2"
                              onClick={() => handleRemoveIndicator(idx)} title="Supprimer">
                              <FaTrash />
                            </button>
                            <div>
                              <span className="me-2 fw-bold">#{idx + 1}</span>
                              <span className="me-2 small">
                                <b>Préfab :</b> {indPrefab.length > 0 ? indPrefab.join(" › ") : "Non"}
                              </span>
                              <br />
                              <span className="me-2 small text-secondary">
                                Pos° : [{ind.indicatorPos?.x || 0}, {ind.indicatorPos?.y || 0}, {ind.indicatorPos?.z || 0}]
                              </span>
                              <span className="me-2 small text-secondary">
                                Rot° : [{ind.indicatorRot?.x || 0}, {ind.indicatorRot?.y || 0}, {ind.indicatorRot?.z || 0}]
                              </span>
                              <span className="me-2 small text-secondary">
                                Ech. : {ind.indicatorPrefabScale}/1
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label>Pas d'indicateur...</label>
                    <p className="small text-secondary">◀ Configurez un indicateur à gauche et ajoutez-le</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ===== SECTION RÉPONSES ===== */}
          <div className="border border-primary rounded p-3 mb-4">
            <h4 className="text-primary text-center">Réponses</h4>
            <p className="small text-secondary text-center">
              Définissez les bonnes et mauvaises réponses pour cette question.
            </p>

            <div className="row">
              {/* GAUCHE — formulaire réponse */}
              <div className="col">
                <div className="row">
                  <div className="col-md-8">
                    <TextInput
                      label="Choix"
                      value={currentQuizChoice.choice}
                      onChange={(e) => handleQuizChoiceChange("choice", e.target.value)}
                      className="mb-3"
                      placeholder="Ex: Propreté de l'espace de travail"
                    />
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label fw-bold">Statut</label>
                      <select className="form-select"
                        value={currentQuizChoice.correct ? "true" : "false"}
                        onChange={(e) => handleQuizChoiceChange("correct", e.target.value)}>
                        <option value="true">Vrai</option>
                        <option value="false">Faux</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <TextArea
                    label="Réponse"
                    value={currentQuizChoice.answer || ""}
                    onChange={(e) => handleQuizChoiceChange("answer", e.target.value)}
                    placeholder="Explication du statut de la réponse (pourquoi est-ce vrai ou faux ?)"
                  />
                </div>

                <div className="row">
                  <div className="col-md-8" title="Indicateurs optionnels positionnés sur le modèle 3D">
                    <label className="form-label">
                      <i className="mdi mdi-map-marker-outline" /> Type d'indicateur ℹ️
                    </label>
                    <div className="d-flex gap-2 mb-2 flex-wrap">
                      {["zone", "niveau", "aplomb", "point"].map((opt) => (
                        <button key={opt} type="button"
                          className={`btn btn-sm ${prefab[0] === opt ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => handleChoiceFeedbackPrefabChange("main", opt)}>
                          {opt}
                        </button>
                      ))}
                      {prefab.length > 0 && (
                        <button type="button" className="btn btn-sm btn-outline-danger"
                          onClick={() => setCurrentQuizChoice((prev) => ({
                            ...prev,
                            choiceFeedbacks: { ...prev.choiceFeedbacks, feedbackPrefab: [] },
                          }))} title="Retirer l'indicateur">
                          ✕ Aucun
                        </button>
                      )}
                    </div>

                    {prefab[0] === "point" && (
                      <div className="ps-2 border-start border-primary mt-2">
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <small className="text-secondary">Direction :</small>
                          {["in", "out"].map((opt) => (
                            <button key={opt} type="button"
                              className={`btn btn-sm ${prefab[1] === opt ? "btn-primary" : "btn-outline-secondary"}`}
                              onClick={() => handleChoiceFeedbackPrefabChange("direction", opt)}>
                              {opt}
                            </button>
                          ))}
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <small className="text-secondary">Orientation :</small>
                          {[["vertical", "Vertical"], ["horizontal", "Horizontal"]].map(([val, label]) => (
                            <button key={val} type="button"
                              className={`btn btn-sm ${prefab[2] === val ? "btn-primary" : "btn-outline-secondary"}`}
                              onClick={() => handleChoiceFeedbackPrefabChange("orientation", val)}>
                              {label}
                            </button>
                          ))}
                        </div>
                        {/* <div className="d-flex align-items-center gap-2">
                          <small className="text-secondary">Distance :</small>
                          <button type="button"
                            className={`btn btn-sm ${prefab.includes("distance") ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => handleChoiceFeedbackPrefabChange("distance", !prefab.includes("distance"))}>
                            {prefab.includes("distance") ? "✔ Oui" : "Non"}
                          </button>
                        </div> */}
                      </div>
                    )}
                  </div>

                  <div className="col-md-2" title="Échelle de l'indicateur (base : 1cm)">
                    <TextInput label="Échelle ℹ️" type="text"
                      value={String(currentQuizChoice.choiceFeedbacks.feedbackPrefabScale)}
                      onChange={(e) => handleQuizChoiceChange("feedbackPrefabScale", e.target.value)} />
                  </div>


                  {prefab.length > 0 && (
                    <div className="col-md-12 mt-3">
                      <button type="button"
                        className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                        onClick={() => setShowTransformMenu(!showTransformMenu)}>
                        <span><i className="mdi mdi-cog-outline me-2" />Position & Rotation</span>
                        <i className={`mdi ${showTransformMenu ? "mdi-chevron-up" : "mdi-chevron-down"}`} />
                      </button>
                      {showTransformMenu && (
                        <div className="mt-3 p-3 border rounded bg-light">
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label small fw-bold">
                                <i className="mdi mdi-axis-arrow" /> Position (x, y, z)
                              </label>
                              <div className="d-flex gap-2">
                                {["x", "y", "z"].map((axis) => (
                                  <div key={axis} className="flex-fill">
                                    <TextInput label={axis.toUpperCase()} type="text"
                                      value={String(currentQuizChoice.choiceFeedbacks.feedbackPos?.[axis] ?? 0)}
                                      onChange={(e) => handleQuizChoiceChange(axis, e.target.value)} />
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small fw-bold">
                                <i className="mdi mdi-rotate-3d" /> Rotation (x, y, z)
                              </label>
                              <div className="d-flex gap-2">
                                {[["rx", "X"], ["ry", "Y"], ["rz", "Z"]].map(([key, label]) => (
                                  <div key={key} className="flex-fill">
                                    <TextInput label={label} type="text"
                                      value={String(currentQuizChoice.choiceFeedbacks.feedbackRot?.[key.replace("r", "")] ?? 0)}
                                      onChange={(e) => handleQuizChoiceChange(key, e.target.value)} />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="col d-flex justify-content-end my-3">
                    {editingQuizChoiceIndex !== null && (
                      <button type="button" className="btn btn-sm btn-outline-secondary mt-3 me-2"
                        onClick={handleCancelQuizChoiceEdit}>
                        Annuler
                      </button>
                    )}
                    <button type="button" className="btn btn-sm btn-outline-primary mt-3"
                      onClick={handleAddQuizChoice}
                      disabled={
                        currentQuizChoice.choice.trim() === "" ||
                        (prefab[0] === "point" && (!prefab[1] || !prefab[2]))
                      }>
                      {currentQuizChoice.choice.trim() === ""
                        ? "⚠️ Nom du choix obligatoire ⚠️"
                        : prefab[0] === "point" && !prefab[1]
                          ? "◀ Choisir une direction"
                          : prefab[0] === "point" && !prefab[2]
                            ? "◀ Choisir une orientation"
                            : editingQuizChoiceIndex !== null
                              ? "Modifier la réponse ▶"
                              : "+ Ajouter la réponse ▶"}
                    </button>
                  </div>
                </div>

                <p className="small text-secondary text-center">
                  Les indicateurs (feedbacks) sont les éléments visuels <b>optionnels</b>, placés dans l'espace.<br />
                  Ils peuvent être des points ou des zones, et permettent d'indiquer le ou les emplacements de l'action.
                </p>
              </div>

              {/* DROITE — liste des réponses */}
              <div className="col">
                {Array.isArray(currentQuiz.quizChoices) && currentQuiz.quizChoices.length > 0 ? (
                  <div>
                    <div className="row mb-2">
                      <div className="col-md-12">
                        <label>Liste des réponses</label>
                        <select className="form-select"
                          value={currentQuiz.isSorted === true ? "true" : "false"}
                          onChange={(e) => setCurrentQuiz((prev) => ({ ...prev, isSorted: e.target.value === "true" }))}>
                          <option value="false" title="Les bonnes réponses sont sélectionnables aléatoirement">Ordre aléatoire</option>
                          <option value="true" title="Les bonnes réponses doivent être sélectionnées dans l'ordre">Respecter l'ordre</option>
                        </select>
                      </div>
                    </div>
                    <div className="sortable-list" ref={sortableRef} data-id="sortable-list">
                      {currentQuiz.quizChoices.map((choice, idx) => {
                        const choicePrefab = Array.isArray(choice.choiceFeedbacks?.feedbackPrefab)
                          ? choice.choiceFeedbacks.feedbackPrefab
                          : choice.choiceFeedbacks?.feedbackPrefab
                            ? [choice.choiceFeedbacks.feedbackPrefab]
                            : [];
                        return (
                          <div key={choice.choiceID}
                            className={"border rounded p-2 mb-1 d-flex align-items-center " + (choice.correct ? "border-success" : "border-danger")}
                            title={choice.correct ? "Réponse correcte" : "Réponse incorrecte"}
                            data-id={choice.choiceID}
                            ref={(el) => (answerRefs.current[choice.choiceID] = el)}>
                            <span className="drag-handle me-2" style={{ cursor: "grab" }} title="Déplacer">
                              <FaGripVertical />
                            </span>
                            <button type="button" className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEditQuizChoice(idx)} title="Modifier">
                              <FaEdit />
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary me-2"
                              onClick={() => handleDuplicateQuizChoice(choice.choiceID, currentQuiz, setCurrentQuiz, setMessage)}
                              title="Dupliquer">
                              <FaCopy />
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger me-2"
                              onClick={() => handleRemoveQuizChoice(idx)} title="Supprimer">
                              <FaTrash />
                            </button>
                            <div>
                              <span className="me-2 fw-bold">#{idx + 1}</span>
                              <span className="me-2 fw-bold">{choice.choice}</span>
                              <br />
                              <span className="me-2 small"><b>Réponse :</b> {choice.answer}</span>
                              <br />
                              <span className="me-2 small">
                                <b>Indicateur :</b> {choicePrefab.length > 0 ? choicePrefab.join(" › ") : "Non"}
                              </span>
                              <span className="me-2 small text-secondary">
                                Pos° : [{choice.choiceFeedbacks.feedbackPos.x || 0}, {choice.choiceFeedbacks.feedbackPos.y || 0}, {choice.choiceFeedbacks.feedbackPos.z || 0}]
                              </span>
                              <span className="me-2 small text-secondary">
                                Rot° : [{choice.choiceFeedbacks.feedbackRot.x || 0}, {choice.choiceFeedbacks.feedbackRot.y || 0}, {choice.choiceFeedbacks.feedbackRot.z || 0}]
                              </span>
                              <span className="me-2 small text-secondary">
                                Ech. : {choice.choiceFeedbacks.feedbackPrefabScale}/1
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label>Pas de réponse...</label>
                    <p className="small text-secondary">◀ Pour ajouter une réponse, saisissez-la à gauche et ajoutez-la à la liste</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Boutons Enregistrer / Annuler */}
          <div className="row mt-4">
            {currentQuiz.quizQuestion ? (
              <div className="d-flex justify-content-center gap-3">
                <button className="btn btn-md btn-outline-secondary" onClick={handleCancelQuiz}>
                  Annuler
                </button>
                <button className="btn btn-md btn-outline-primary" onClick={handleAddQuiz}>
                  Enregistrer la question [{currentQuiz.quizQuestion}]
                </button>
              </div>
            ) : (
              <div className="text-center">
                <button className="btn btn-md btn-outline-primary" disabled>
                  ⚠️ Question manquante ⚠️
                </button>
                <br />
                <span className="small">Pour sauvegarder <b>une question</b> est nécessaire.</span>
              </div>
            )}
            <div className="text-center mt-2">
              <span className="small text-secondary">
                ⚠️ Enregistrez la question en cours avant de sauvegarder le questionnaire (Sinon, vos modifications seront perdues.). ⚠️
              </span>
            </div>
          </div>

        </div>
      </details>
      {isImgModalOpen && (
        <div
          onClick={closeImgModal}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out"
          }}
        >
          <img
            src={imgModalSrc}
            alt="Aperçu grand format"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "8px",
              boxShadow: "0 0 30px rgba(0,0,0,0.6)"
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default QuizzesManage;

//Creators : Florian Cohen-Joly @3DDGAMES -- Under license